import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { solSlideUp, solViewport } from "../shared/solutionMotion";

const pipelineSteps = [
    "Raw Footage",
    "Editing",
    "Color Grading",
    "Effects",
    "Final Export",
];

const LINE_DURATION_S = 0.85;
const STEP_STAGGER_S = 0.2;
const STEP_ANIM_S = 0.48;

export const VeProductionPipelineSection = () => {
    const trackRef = useRef<HTMLDivElement>(null);
    const inView = useInView(trackRef, { ...solViewport, amount: 0.35 });
    const [lineVisible, setLineVisible] = useState(false);
    const [revealedCount, setRevealedCount] = useState(0);
    const [sequenceDone, setSequenceDone] = useState(false);
    const [isVertical, setIsVertical] = useState(false);

    useEffect(() => {
        const mq = window.matchMedia("(max-width: 600px)");
        const update = () => setIsVertical(mq.matches);
        update();
        mq.addEventListener("change", update);
        return () => mq.removeEventListener("change", update);
    }, []);

    useEffect(() => {
        if (!inView) {
            setLineVisible(false);
            setRevealedCount(0);
            setSequenceDone(false);
            return;
        }

        setLineVisible(true);
        setSequenceDone(false);

        const stepTimers = pipelineSteps.map((_, index) =>
            window.setTimeout(
                () => setRevealedCount(index + 1),
                (LINE_DURATION_S + index * STEP_STAGGER_S) * 1000
            )
        );

        const finishTimer = window.setTimeout(
            () => setSequenceDone(true),
            (LINE_DURATION_S + (pipelineSteps.length - 1) * STEP_STAGGER_S + STEP_ANIM_S) * 1000
        );

        return () => {
            stepTimers.forEach(window.clearTimeout);
            window.clearTimeout(finishTimer);
        };
    }, [inView]);

    return (
        <section className="ve-pipeline">
            <div className="sol-container">
                <motion.div
                    className="ve-section-head ve-section-head--center"
                    initial="hidden"
                    whileInView="visible"
                    viewport={solViewport}
                    variants={solSlideUp}
                >
                    <h2 className="ve-h2">Production Pipeline</h2>
                    <p className="ve-sub">
                        From raw footage to polished final export — every step handled in-house.
                    </p>
                </motion.div>

                <div className="ve-pipeline__track" ref={trackRef}>
                    <motion.div
                        className="ve-pipeline__line"
                        aria-hidden
                        initial={
                            isVertical
                                ? { scaleY: 0, opacity: 0 }
                                : { scaleX: 0, opacity: 0 }
                        }
                        animate={
                            lineVisible
                                ? isVertical
                                    ? { scaleY: 1, opacity: 1 }
                                    : { scaleX: 1, opacity: 1 }
                                : isVertical
                                  ? { scaleY: 0, opacity: 0 }
                                  : { scaleX: 0, opacity: 0 }
                        }
                        transition={{
                            duration: LINE_DURATION_S,
                            ease: [0.22, 1, 0.36, 1],
                        }}
                    />

                    <div className="ve-pipeline__steps">
                        {pipelineSteps.map((step, index) => {
                            const isRevealed = revealedCount > index;
                            const isActive =
                                !sequenceDone && revealedCount === index + 1;
                            const isComplete =
                                sequenceDone ? isRevealed : revealedCount > index + 1;

                            return (
                                <motion.div
                                    key={step}
                                    className="ve-pipeline__step"
                                    initial={false}
                                    animate={{
                                        opacity: isRevealed ? 1 : 0,
                                        y: isRevealed ? 0 : 22,
                                    }}
                                    transition={{
                                        duration: STEP_ANIM_S,
                                        ease: [0.22, 1, 0.36, 1],
                                    }}
                                    aria-hidden={!isRevealed}
                                >
                                    <div
                                        className={[
                                            "ve-pipeline__node",
                                            isActive ? "is-active" : "",
                                            isComplete ? "is-complete" : "",
                                        ]
                                            .filter(Boolean)
                                            .join(" ")}
                                    >
                                        {index + 1}
                                    </div>
                                    <span className="ve-pipeline__label">{step}</span>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
};
